/**
 * CampaignAI — local AI opponent for single-player campaign mode.
 *
 * 10 distinct opponents based on the original Dr. Robotnik's Mean Bean Machine.
 * Key design:
 *  - Evaluation uses cluster-size² scoring (incentivises building 4-groups).
 *  - Difficulties 6-10 use 2-step look-ahead (plan current + next piece).
 *  - colorAdj weight dominates height so the AI keeps clustering even late-game.
 */

import { GameLogic } from './GameLogic'
import { Board } from './Board'
import { detectGroups } from './GroupDetector'
import {
  BOARD_COLS, BOARD_ROWS, CELL_EMPTY, CELL_GARBAGE,
  SUB_OFFSETS,
} from '../constants'

export interface AIDecision {
  targetCol: number
  targetRot: number
}

// ─── Simulation helpers ───────────────────────────────────────────────────────

function isValidPos(board: Board, c1: number, r1: number, c2: number, r2: number): boolean {
  return (
    board.inBounds(c1, r1) && board.isEmpty(c1, r1) &&
    board.inBounds(c2, r2) && board.isEmpty(c2, r2)
  )
}

function findDropRow(board: Board, col: number, rot: number): number | null {
  const [dc, dr] = SUB_OFFSETS[rot]
  const startRow = dr < 0 ? -dr : 0
  if (!isValidPos(board, col, startRow, col + dc, startRow + dr)) return null
  let row = startRow
  while (isValidPos(board, col, row + 1, col + dc, row + 1 + dr)) row++
  return row
}

function simulate(
  board: Board, col: number, rot: number,
  color1: number, color2: number,
): Board | null {
  const [dc, dr] = SUB_OFFSETS[rot]
  const row = findDropRow(board, col, rot)
  if (row === null) return null
  const clone = board.clone()
  clone.set(col,      row,      color1)
  clone.set(col + dc, row + dr, color2)
  clone.applyGravity()
  return clone
}

function allValidMoves(board: Board): AIDecision[] {
  const moves: AIDecision[] = []
  for (let rot = 0; rot < 4; rot++) {
    for (let col = 0; col < BOARD_COLS; col++) {
      if (findDropRow(board, col, rot) !== null) {
        moves.push({ targetCol: col, targetRot: rot })
      }
    }
  }
  return moves
}

// ─── Chain simulation ─────────────────────────────────────────────────────────

function countChainWaves(board: Board): number {
  const b = board.clone()
  let waves = 0
  for (let safety = 0; safety < 20; safety++) {
    const groups = detectGroups(b)
    if (groups.length === 0) break
    waves++
    for (const g of groups) {
      for (const [c, r] of g.cells) b.set(c, r, CELL_EMPTY)
    }
    b.applyGravity()
  }
  return waves
}

// ─── Board evaluation ─────────────────────────────────────────────────────────

interface EvalWeights {
  height:   number
  holes:    number
  bump:     number
  cluster:  number   // weight for sum(groupSize²) — rewards growing groups
  chains:   number   // weight for chainWaves²
}

/**
 * Evaluate a board position.
 * cluster scoring: each connected same-color group of size S contributes S².
 * This incentivises the AI to grow groups (group of 3→4 = +7 pts per unit weight,
 * group of 2→3 = +5), which naturally leads to chain setups.
 */
function evalBoard(board: Board, w: EvalWeights, simChains: boolean): number {
  const cells = board.cells
  const heights = new Array<number>(BOARD_COLS).fill(0)

  for (let col = 0; col < BOARD_COLS; col++) {
    for (let row = 0; row < BOARD_ROWS; row++) {
      if (cells[row * BOARD_COLS + col] !== CELL_EMPTY) {
        heights[col] = BOARD_ROWS - row
        break
      }
    }
  }

  const aggregateHeight = heights.reduce((s, h) => s + h, 0)

  let holes = 0
  for (let col = 0; col < BOARD_COLS; col++) {
    let filled = false
    for (let row = 0; row < BOARD_ROWS; row++) {
      if (cells[row * BOARD_COLS + col] !== CELL_EMPTY) filled = true
      else if (filled) holes++
    }
  }

  let bumpiness = 0
  for (let col = 0; col < BOARD_COLS - 1; col++) {
    bumpiness += Math.abs(heights[col] - heights[col + 1])
  }

  // Cluster scoring: BFS each connected same-color component, score = size²
  const visited = new Uint8Array(BOARD_COLS * BOARD_ROWS)
  let clusterScore = 0
  const stack: number[] = []

  for (let idx = 0; idx < BOARD_COLS * BOARD_ROWS; idx++) {
    if (visited[idx]) continue
    const v = cells[idx]
    if (v === CELL_EMPTY || v === CELL_GARBAGE) continue

    stack.length = 0
    stack.push(idx)
    visited[idx] = 1
    let size = 0

    while (stack.length > 0) {
      const i = stack.pop()!
      size++
      const c = i % BOARD_COLS
      const r = (i / BOARD_COLS) | 0
      if (c > 0             && !visited[i - 1]               && cells[i - 1]               === v) { visited[i - 1]               = 1; stack.push(i - 1) }
      if (c < BOARD_COLS-1  && !visited[i + 1]               && cells[i + 1]               === v) { visited[i + 1]               = 1; stack.push(i + 1) }
      if (r > 0             && !visited[i - BOARD_COLS]       && cells[i - BOARD_COLS]       === v) { visited[i - BOARD_COLS]       = 1; stack.push(i - BOARD_COLS) }
      if (r < BOARD_ROWS-1  && !visited[i + BOARD_COLS]       && cells[i + BOARD_COLS]       === v) { visited[i + BOARD_COLS]       = 1; stack.push(i + BOARD_COLS) }
    }

    clusterScore += size * size   // S²: growing groups is exponentially more valuable
  }

  const chainWaves = simChains ? countChainWaves(board) : 0

  return (
    - w.height  * aggregateHeight
    - w.holes   * holes
    - w.bump    * bumpiness
    + w.cluster * clusterScore
    + w.chains  * chainWaves * chainWaves
  )
}

// ─── Opponent profiles ────────────────────────────────────────────────────────

interface OpponentProfile {
  weights:      EvalWeights
  noise:        number
  simChains:    boolean
  lookahead:    boolean   // use 2-step lookahead
  thinkDelay:   number
  moveInterval: number
  dropStyle:    0 | 1 | 2  // 0=natural fall, 1=soft drop, 2=hard drop
}

// cluster weight >> height weight so AI keeps clustering late-game.
// With S²: a group of 3 cells = 9 pts. cluster weight of 0.5 → 4.5 pts.
// Height of those 3 cells costs 0.45 * 3 = 1.35 pts. Cluster wins.
const PROFILES: Record<number, OpponentProfile> = {
  // 3 – Coconuts: only cares about color clustering, blind to everything else
  3: {
    weights:   { height: 0.05, holes: 0,    bump: 0,    cluster: 0.35, chains: 0    },
    noise: 240, simChains: false, lookahead: false, thinkDelay: 0.50, moveInterval: 0.18, dropStyle: 0,
  },
  // 4 – Bat Brain: survival + some clustering, no chain planning
  4: {
    weights:   { height: 0.30, holes: 0.22, bump: 0.08, cluster: 0.35, chains: 0    },
    noise: 170, simChains: false, lookahead: false, thinkDelay: 0.42, moveInterval: 0.16, dropStyle: 0,
  },
  // 5 – Grounder: first chain awareness
  5: {
    weights:   { height: 0.32, holes: 0.25, bump: 0.10, cluster: 0.45, chains: 8    },
    noise: 120, simChains: true,  lookahead: false, thinkDelay: 0.32, moveInterval: 0.12, dropStyle: 0,
  },
  // 6 – Spike: 2-step lookahead begins, soft-drops
  6: {
    weights:   { height: 0.34, holes: 0.28, bump: 0.12, cluster: 0.55, chains: 14   },
    noise:  80, simChains: true,  lookahead: true,  thinkDelay: 0.26, moveInterval: 0.10, dropStyle: 1,
  },
  // 7 – Sir Fuzzy: stronger chain reward
  7: {
    weights:   { height: 0.36, holes: 0.30, bump: 0.13, cluster: 0.65, chains: 22   },
    noise:  50, simChains: true,  lookahead: true,  thinkDelay: 0.20, moveInterval: 0.08, dropStyle: 1,
  },
  // 8 – Dragon Breath: chain reward dominates, hard-drops
  8: {
    weights:   { height: 0.38, holes: 0.32, bump: 0.14, cluster: 0.80, chains: 34   },
    noise:  22, simChains: true,  lookahead: true,  thinkDelay: 0.12, moveInterval: 0.06, dropStyle: 2,
  },
  // 9 – Humpty: near-perfect
  9: {
    weights:   { height: 0.40, holes: 0.33, bump: 0.15, cluster: 1.00, chains: 50   },
    noise:   8, simChains: true,  lookahead: true,  thinkDelay: 0.08, moveInterval: 0.04, dropStyle: 2,
  },
  // 10 – Dr. Robotnik: zero noise, maximum chain drive
  10: {
    weights:   { height: 0.42, holes: 0.34, bump: 0.15, cluster: 1.20, chains: 70   },
    noise:   0, simChains: true,  lookahead: true,  thinkDelay: 0.04, moveInterval: 0.03, dropStyle: 2,
  },
}

// ─── CampaignAI ───────────────────────────────────────────────────────────────

export class CampaignAI {
  private lastSpawnCount: number = -1
  private decision:      AIDecision | null = null
  private decisionMade:  boolean = false
  private positionDone:  boolean = false
  private thinkTimer:    number  = 0
  private moveTimer:     number  = 0
  private fixedCursor:   number  = 0

  constructor(private readonly difficulty: number) {}

  update(dt: number, game: GameLogic): void {
    const phase = game.phase.kind

    // Detect new piece spawn via spawnCount (reliable even when phase stays 'falling')
    if (game.spawnCount !== this.lastSpawnCount) {
      this.lastSpawnCount = game.spawnCount
      this.decision     = null
      this.decisionMade = false
      this.positionDone = false
      this.thinkTimer   = 0
      this.moveTimer    = 0
    }

    if (phase !== 'falling') return

    this.thinkTimer += dt
    if (this.thinkTimer < this.getThinkDelay()) return

    if (!this.decisionMade) {
      this.decision     = this.computeDecision(game)
      this.decisionMade = true
    }

    if (this.positionDone) {
      this.executePostPosition(game)
      return
    }

    this.moveTimer += dt
    if (this.moveTimer >= this.getMoveInterval()) {
      this.moveTimer = 0
      if (this.moveToTarget(game)) this.positionDone = true
    }
  }

  // ─── Timing ────────────────────────────────────────────────────────────────

  private getThinkDelay(): number {
    if (this.difficulty <= 2) return [0, 0.55, 0.50][this.difficulty]
    return PROFILES[this.difficulty]?.thinkDelay ?? 0.10
  }

  private getMoveInterval(): number {
    if (this.difficulty <= 2) return 0.20
    return PROFILES[this.difficulty]?.moveInterval ?? 0.09
  }

  // ─── Post-position action ──────────────────────────────────────────────────

  private executePostPosition(game: GameLogic): void {
    const style = this.difficulty <= 2 ? 0 : (PROFILES[this.difficulty]?.dropStyle ?? 0)
    if (style === 2) {
      game.hardDrop()
    } else if (style === 1) {
      game.softDropStart()
    }
  }

  // ─── Move execution ────────────────────────────────────────────────────────

  private moveToTarget(game: GameLogic): boolean {
    if (!this.decision) return true
    const { targetCol, targetRot } = this.decision
    const cur = game.current

    const rotDiff = (targetRot - cur.rotation + 4) % 4
    if (rotDiff !== 0) {
      if (rotDiff <= 2) game.rotateCW(); else game.rotateCCW()
      return false
    }
    if (cur.col < targetCol) { game.moveRight(); return false }
    if (cur.col > targetCol) { game.moveLeft();  return false }
    return true
  }

  // ─── Decision ─────────────────────────────────────────────────────────────

  private computeDecision(game: GameLogic): AIDecision {
    switch (this.difficulty) {
      case 1:  return this.strategyCrabmeat(game)
      case 2:  return this.strategyBuzzBomber(game)
      default: return this.strategyEval(game)
    }
  }

  /** Crabmeat: completely random valid placement. */
  private strategyCrabmeat(game: GameLogic): AIDecision {
    const moves = allValidMoves(game.board)
    if (moves.length === 0) return { targetCol: 2, targetRot: 0 }
    return moves[Math.floor(Math.random() * moves.length)]
  }

  /** Buzz Bomber: sweeps columns left→right flat. No chain setup. */
  private strategyBuzzBomber(game: GameLogic): AIDecision {
    const maxCol = BOARD_COLS - 2  // rot=1: sub is to the right
    for (let attempt = 0; attempt <= maxCol; attempt++) {
      const col = (this.fixedCursor + attempt) % (maxCol + 1)
      if (findDropRow(game.board, col, 1) !== null) {
        this.fixedCursor = col + 1
        return { targetCol: col, targetRot: 1 }
      }
    }
    const moves = allValidMoves(game.board)
    return moves.length > 0 ? moves[0] : { targetCol: 2, targetRot: 0 }
  }

  /** Difficulties 3-10: evaluation-based with optional 2-step lookahead. */
  private strategyEval(game: GameLogic): AIDecision {
    const diff    = Math.max(3, Math.min(10, this.difficulty))
    const profile = PROFILES[diff]
    const moves   = allValidMoves(game.board)
    if (moves.length === 0) return { targetCol: 2, targetRot: 0 }

    const noise = profile.noise

    if (profile.lookahead) {
      return this.evalWithLookahead(game, moves, profile, noise)
    }

    let best      = moves[0]
    let bestScore = -Infinity
    for (const move of moves) {
      const sim = simulate(game.board, move.targetCol, move.targetRot,
                           game.current.color1, game.current.color2)
      if (!sim) continue
      const score = evalBoard(sim, profile.weights, profile.simChains)
                  + (Math.random() - 0.5) * noise
      if (score > bestScore) { bestScore = score; best = move }
    }
    return best
  }

  /**
   * 2-step lookahead: for each placement of the current piece, find the best
   * placement of the next piece and take the sum.
   * Picks the current-piece placement that maximises combined score.
   */
  private evalWithLookahead(
    game: GameLogic,
    moves: AIDecision[],
    profile: OpponentProfile,
    noise: number,
  ): AIDecision {
    const { color1: c1, color2: c2 } = game.current
    const { color1: n1, color2: n2 } = game.nextPiece

    let best      = moves[0]
    let bestScore = -Infinity

    for (const move of moves) {
      const sim1 = simulate(game.board, move.targetCol, move.targetRot, c1, c2)
      if (!sim1) continue

      // Score after placing current piece
      const score1 = evalBoard(sim1, profile.weights, profile.simChains)

      // Best score achievable by placing the next piece on top of sim1
      let bestNext = -Infinity
      const nextMoves = allValidMoves(sim1)
      for (const nm of nextMoves) {
        const sim2 = simulate(sim1, nm.targetCol, nm.targetRot, n1, n2)
        if (!sim2) continue
        const s2 = evalBoard(sim2, profile.weights, profile.simChains)
        if (s2 > bestNext) bestNext = s2
      }

      // Combined score: current + discounted future
      const combined = score1 + 0.8 * (bestNext === -Infinity ? score1 : bestNext)
                     + (Math.random() - 0.5) * noise

      if (combined > bestScore) { bestScore = combined; best = move }
    }

    return best
  }
}
